'use client';

import { useTranslation } from '@/context/LanguageContext';
import Link from 'next/link';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAward, faPencilAlt, faUserCheck, faUserPlus, faEdit, faUpload,
  faCreditCard, faIdCard, faCheck, faArrowRight, faExternalLinkAlt, faEnvelope,
  faPlane, faCity, faBus
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

export default function PendaftaranContent() {
  const { t, language } = useTranslation();
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'Universitas Mulawarman';
  const siteNameEnglish = process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || 'Mulawarman University';
  const currentSiteName = language === 'en' ? siteNameEnglish : siteName;

  const admissionPaths = [
    {
      code: 'SNBP',
      title: t('admission.path.snbp.title'),
      icon: 'award',
      color: 'bg-blue-500',
      desc: t('admission.path.snbp.desc'),
      features: ['Nilai Rapor Semester 1-5', 'Prestasi Akademik/Non-Akademik', 'Portofolio (untuk prodi tertentu)'],
      timeline: 'Februari - Maret 2025',
      link: 'https://snpmb.bppp.kemdikbud.go.id',
    },
    {
      code: 'SNBT',
      title: t('admission.path.snbt.title'),
      icon: 'pencil-alt',
      color: 'bg-unmulYellow text-unmulDarkGreen',
      desc: t('admission.path.snbt.desc'),
      features: ['Tes Potensi Skolastik', 'Literasi Bahasa Indonesia & Inggris', 'Penalaran Matematika'],
      timeline: 'April - Juni 2025',
      link: 'https://snpmb.bppp.kemdikbud.go.id',
      popular: true,
    },
    {
      code: 'SMMPTN',
      title: t('admission.path.mandiri.title'),
      icon: 'user-check',
      color: 'bg-unmulGreen',
      desc: t('admission.path.mandiri.desc', { siteName: currentSiteName }),
      features: ['Ujian Tulis Mandiri', 'Jalur Prestasi', 'Jalur Afirmasi Daerah'],
      timeline: 'Juli - Agustus 2025',
      link: 'https://pmb.unmul.ac.id',
    },
  ];

  const steps = [
    { num: 1, title: t('admission.step.1.title'), desc: t('admission.step.1.desc'), icon: 'user-plus' },
    { num: 2, title: t('admission.step.2.title'), desc: t('admission.step.2.desc'), icon: 'edit' },
    { num: 3, title: t('admission.step.3.title'), desc: t('admission.step.3.desc'), icon: 'upload' },
    { num: 4, title: t('admission.step.4.title'), desc: t('admission.step.4.desc'), icon: 'credit-card' },
    { num: 5, title: t('admission.step.5.title'), desc: t('admission.step.5.desc'), icon: 'id-card' },
  ];

  const iconMap: { [key: string]: any } = {
    'award': faAward,
    'pencil-alt': faPencilAlt,
    'user-check': faUserCheck,
    'user-plus': faUserPlus,
    'edit': faEdit,
    'upload': faUpload,
    'credit-card': faCreditCard,
    'id-card': faIdCard,
  };

  return (
    <>
      {/* Hero */}
      <section className="pt-36 pb-20 bg-gradient-to-br from-unmulDarkGreen to-unmulGreen text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-red-500 px-4 py-2 rounded-full text-sm font-bold mb-4">
              <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
              {t('admission.status.open')}
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">
              {t('admission.hero.title')} <span className="text-unmulYellow">2025/2026</span>
            </h1>
            <p className="text-xl text-green-100 mb-8">
              {t('admission.hero.subtitle', { siteName: currentSiteName })}
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="https://pmb.unmul.ac.id" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-unmulYellow text-unmulDarkGreen font-bold rounded-lg hover:bg-white transition">
                <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" /> {t('admission.portal')}
              </a>
              <a href="#jalur" className="px-6 py-3 border-2 border-white/50 rounded-lg font-bold hover:bg-white/10 transition">
                {t('admission.viewPaths')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Admission Paths */}
      <section id="jalur" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">{t('admission.paths.title', { siteName: currentSiteName })}</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            {t('admission.paths.subtitle')}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {admissionPaths.map((path, i) => (
              <div key={i} className={`bg-white rounded-2xl shadow-lg overflow-hidden ${path.popular ? 'ring-2 ring-unmulYellow' : ''}`}>
                {path.popular && (
                  <div className="bg-red-500 text-white text-center text-sm font-bold py-2">
                    {t('admission.popular')}
                  </div>
                )}
                <div className="p-8">
                  <div className={`w-16 h-16 ${path.color} text-white rounded-2xl flex items-center justify-center text-2xl mb-4`}>
                    <FontAwesomeIcon icon={iconMap[path.icon]} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-1">{path.code}</h3>
                  <p className="text-sm text-unmulGreen font-medium mb-4">{path.title}</p>
                  <p className="text-gray-600 text-sm mb-6">{path.desc}</p>

                  <ul className="space-y-2 mb-6">
                    {path.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <FontAwesomeIcon icon={faCheck} className="text-unmulGreen mt-1" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-4 border-t">
                    <div className="text-xs text-gray-500 mb-2">{t('admission.timeline')}</div>
                    <div className="font-bold text-gray-800">{path.timeline}</div>
                  </div>

                  <a href={path.link} target="_blank" rel="noopener noreferrer" className={`mt-6 block w-full py-3 text-center rounded-lg font-bold transition ${path.popular ? 'bg-unmulYellow text-unmulDarkGreen hover:bg-yellow-400' : 'bg-unmulGreen text-white hover:bg-unmulDarkGreen'}`}>
                    {t('admission.moreInfo')} <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">{t('admission.steps.title')}</h2>

          <div className="flex flex-wrap justify-center gap-6">
            {steps.map((step, i) => (
              <div key={i} className="flex flex-col items-center w-40">
                <div className="w-16 h-16 bg-unmulGreen text-white rounded-full flex items-center justify-center text-xl font-bold mb-4 relative">
                  {step.num}
                  {i < steps.length - 1 && (
                    <div className="hidden md:block absolute left-full top-1/2 w-10 h-0.5 bg-gray-200"></div>
                  )}
                </div>
                <h4 className="font-bold text-gray-800 text-center">{step.title}</h4>
                <p className="text-xs text-gray-500 text-center mt-1">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-unmulDarkGreen text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('admission.cta.title')}</h2>
          <p className="text-green-200 mb-8 max-w-xl mx-auto">
            {t('admission.cta.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://wa.me/6281234567890" className="px-6 py-3 bg-green-500 rounded-lg font-bold hover:bg-green-600 transition">
              <FontAwesomeIcon icon={faWhatsapp} className="mr-2" /> WhatsApp
            </a>
            <a href="mailto:pmb@unmul.ac.id" className="px-6 py-3 border-2 border-white/50 rounded-lg font-bold hover:bg-white/10 transition">
              <FontAwesomeIcon icon={faEnvelope} className="mr-2" /> pmb@unmul.ac.id
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
